import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import React, { useEffect, useRef } from "react";
import LoomVideo from "./LoomVideo";
import { mainLoomVideoUrl } from "../data/siteMedia";
import { getChatConversationId } from "../utils/chatConversation";

const resumeSnippets = [
  {
    title: "Summary",
    text: "Berkley is a full-stack developer with 5+ years of experience in TypeScript, React, Node.js, Python, and database design. He also has Salesforce and Web3/Solana experience plus CPA exam credentials.",
  },
  {
    title: "TaxRise",
    text: "At TaxRise (Aug 2023 to Jul 2024), he built production full-stack apps using React, Node.js/Python APIs, and PostgreSQL/MySQL for thousands of users. He shipped Salesforce components, Apex logic, flows, and test coverage.",
  },
  {
    title: "SolTrade Systems",
    text: "At SolTrade Systems (Dec 2024 to current), he built automated crypto trading systems with Node.js, Python, and Solana APIs with reported monthly profit around $4,000 and over $20,000 realized profit.",
  },
  {
    title: "DLZP Group",
    text: "At DLZP Group (Aug 2025 to current), he designed an AI chatbot with RAG, Python, AWS Lambda, S3 vector buckets, Bedrock, and dynamic system prompts. He also built Angular frontends, Kanban features, and serverless AWS backends.",
  },
  {
    title: "TEKware",
    text: "At TEKware (Aug 2021 to Jul 2023), he developed SafetyAuditPrep using React, Node.js APIs, MongoDB, and external integrations including Quest, TazWorks, FMCSA, Google Maps, and Authorize.NET.",
  },
  {
    title: "Education",
    text: "Education includes Springboard Software Engineering Program (2021), University of Idaho BS in Accounting and Finance (2012, GPA 3.5), and all 4 parts of the CPA exam passed.",
  },
  {
    title: "Contact",
    text: "Berkley is based in Boise, Idaho. Email: olms2074@gmail.com. Portfolio: www.berkleyolmstead.com. LinkedIn and GitHub are available from the site.",
  },
];

const stopWords = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "has",
  "he",
  "in",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "to",
  "was",
  "with",
  "what",
  "which",
  "who",
  "his",
  "about",
  "does",
  "do",
  "you",
]);

const useStyles = makeStyles((theme) => ({
  homeContainer: {
    background:
      "linear-gradient(135deg, #0A0A0A 0%, #141414 50%, #0A0A0A 100%)",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(0, 255, 65, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 75% 75%, rgba(0, 255, 65, 0.02) 0%, transparent 50%)
      `,
      pointerEvents: "none",
    },
  },
  contentWrapper: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    padding: "40px 20px",
  },
  greeting: {
    color: "#B0B0B0",
    fontSize: "1.2rem",
    fontWeight: 400,
    marginBottom: "16px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  firstName: {
    fontWeight: 700,
    color: "#FFFFFF",
    fontSize: "clamp(2.5rem, 8vw, 4.5rem)",
    lineHeight: 1.1,
    marginBottom: "8px",
  },
  lastName: {
    fontWeight: 700,
    fontSize: "clamp(2.5rem, 8vw, 4.5rem)",
    lineHeight: 1.1,
    marginBottom: "24px",
    color: "#00FF41",
  },
  caption: {
    color: "#B0B0B0",
    fontSize: "1.3rem",
    fontWeight: 400,
    marginBottom: "24px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.1rem",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  primaryButton: {
    background: "#00FF41",
    color: "#000000",
    borderRadius: "8px",
    padding: "14px 32px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#00CC33",
      transform: "translateY(-2px)",
      boxShadow: "0 10px 30px rgba(0, 255, 65, 0.3)",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#FFFFFF",
    borderRadius: "8px",
    padding: "14px 32px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    border: "1px solid #2A2A2A",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.1)",
      transform: "translateY(-2px)",
    },
  },
  chatLauncher: {
    position: "fixed",
    right: "24px",
    bottom: "24px",
    zIndex: 30,
    background: "#00FF41",
    color: "#000000",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: "0 12px 24px rgba(0, 0, 0, 0.35)",
    "&:hover": {
      background: "#00CC33",
      transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("xs")]: {
      right: "14px",
      bottom: "14px",
      padding: "10px 14px",
      fontSize: "0.85rem",
    },
  },
  chatWidget: {
    position: "fixed",
    right: "24px",
    bottom: "86px",
    width: "380px",
    maxHeight: "68vh",
    display: "flex",
    flexDirection: "column",
    background: "rgba(10, 10, 10, 0.88)",
    border: "1px solid #2A2A2A",
    borderRadius: "16px",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.25)",
    opacity: 0,
    pointerEvents: "none",
    transform: "translateY(14px)",
    transition: "opacity 0.22s ease, transform 0.22s ease",
    overflow: "hidden",
    zIndex: 29,
    [theme.breakpoints.down("xs")]: {
      left: "12px",
      right: "12px",
      width: "auto",
      bottom: "74px",
      maxHeight: "70vh",
    },
  },
  chatWidgetOpen: {
    opacity: 1,
    pointerEvents: "auto",
    transform: "translateY(0)",
  },
  chatHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #1E1E1E",
    background: "rgba(20, 20, 20, 0.8)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  chatHeaderText: {
    paddingRight: "10px",
  },
  chatTitle: {
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "1rem",
    textAlign: "left",
    marginBottom: "4px",
  },
  chatSubtitle: {
    color: "#B0B0B0",
    textAlign: "left",
    fontSize: "0.85rem",
  },
  closeButton: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    color: "#B0B0B0",
    border: "1px solid #2A2A2A",
    borderRadius: "8px",
    padding: 0,
    fontSize: "1.1rem",
    lineHeight: 1,
    "&:hover": {
      color: "#FFFFFF",
      borderColor: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.08)",
    },
  },
  quickPromptRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    padding: "12px 16px 0",
  },
  quickPromptButton: {
    border: "1px solid #2A2A2A",
    color: "#B0B0B0",
    borderRadius: "999px",
    textTransform: "none",
    fontSize: "0.78rem",
    padding: "6px 10px",
    minWidth: "auto",
    "&:hover": {
      borderColor: "#00FF41",
      color: "#FFFFFF",
      backgroundColor: "rgba(0, 255, 65, 0.08)",
    },
  },
  messageList: {
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minHeight: "190px",
    maxHeight: "280px",
    overflowY: "auto",
  },
  messageBubble: {
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "0.9rem",
    lineHeight: 1.45,
    maxWidth: "90%",
    whiteSpace: "pre-wrap",
    textAlign: "left",
  },
  assistantBubble: {
    backgroundColor: "#1A1A1A",
    color: "#E0E0E0",
    border: "1px solid #2A2A2A",
    alignSelf: "flex-start",
  },
  userBubble: {
    backgroundColor: "rgba(0, 255, 65, 0.12)",
    color: "#FFFFFF",
    border: "1px solid rgba(0, 255, 65, 0.35)",
    alignSelf: "flex-end",
  },
  chatInputRow: {
    padding: "12px 16px 16px",
    borderTop: "1px solid #1E1E1E",
    display: "flex",
    gap: "10px",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  chatInput: {
    flex: 1,
    background: "#121212",
    border: "1px solid #2A2A2A",
    borderRadius: "10px",
    color: "#FFFFFF",
    outline: "none",
    padding: "12px 14px",
    fontSize: "0.92rem",
    fontFamily: "inherit",
    "&::placeholder": {
      color: "#707070",
    },
    "&:focus": {
      borderColor: "#00FF41",
      boxShadow: "0 0 0 2px rgba(0, 255, 65, 0.15)",
    },
  },
  sendButton: {
    minWidth: "120px",
    background: "#00FF41",
    color: "#000000",
    borderRadius: "10px",
    fontWeight: 700,
    textTransform: "none",
    "&:hover": {
      background: "#00CC33",
    },
    "&:disabled": {
      background: "#1E1E1E",
      color: "#707070",
    },
  },
  loadingText: {
    color: "#B0B0B0",
    fontSize: "0.8rem",
    textAlign: "left",
    padding: "0 16px 8px",
  },
  scrollIndicator: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    color: "#B0B0B0",
    animation: "$bounce 2s infinite",
    cursor: "pointer",
    transition: "color 0.3s ease",
    "&:hover": {
      color: "#00FF41",
    },
  },
  "@keyframes bounce": {
    "0%, 20%, 50%, 80%, 100%": {
      transform: "translateX(-50%) translateY(0)",
    },
    "40%": {
      transform: "translateX(-50%) translateY(-10px)",
    },
    "60%": {
      transform: "translateX(-50%) translateY(-5px)",
    },
  },
  cursor: {
    display: "inline-block",
    width: "3px",
    height: "1em",
    backgroundColor: "#00FF41",
    marginLeft: "4px",
    animation: "$blink 1s infinite",
    verticalAlign: "middle",
    transform: "translateY(-1px)",
  },
  "@keyframes blink": {
    "0%, 50%": {
      opacity: 1,
    },
    "51%, 100%": {
      opacity: 0,
    },
  },
  link: {
    textDecoration: "none",
  },
}));

function normalizeWords(input) {
  return (input || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word && !stopWords.has(word));
}

function createFallbackReply(question) {
  const lowered = (question || "").toLowerCase();

  if (
    lowered.includes("contact") ||
    lowered.includes("email") ||
    lowered.includes("reach")
  ) {
    return "You can reach Berkley at olms2074@gmail.com, and his LinkedIn/GitHub links are on this page.";
  }

  const tokens = normalizeWords(question);
  const ranked = resumeSnippets
    .map((snippet) => {
      const snippetWords = new Set(
        normalizeWords(`${snippet.title} ${snippet.text}`),
      );
      const overlap = tokens.reduce(
        (score, token) => (snippetWords.has(token) ? score + 1 : score),
        0,
      );
      return { snippet, overlap };
    })
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, 2)
    .filter((item) => item.overlap > 0);

  if (!ranked.length) {
    return "Based on Berkley's resume, he has 5+ years in full-stack development across React, Node.js, Python, AWS, Salesforce, and database systems. Ask about a company, tech stack, or project outcome for specifics.";
  }

  const highlights = ranked.map((item) => `- ${item.snippet.text}`).join("\n");

  return `Here are the most relevant resume highlights:\n${highlights}`;
}

export default function Home() {
  const classes = useStyles();
  const messageListRef = useRef(null);
  const [displayedText, setDisplayedText] = React.useState("");
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [question, setQuestion] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [conversationId] = React.useState(() =>
    getChatConversationId("portfolio-main-chat"),
  );
  const [messages, setMessages] = React.useState([
    {
      role: "assistant",
      content: "Hi, how can I help you?",
    },
  ]);

  const fullText = "Full-Stack Developer";

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex + 1));
        currentIndex += 1;
      } else {
        clearInterval(typingInterval);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, []);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, isSending, isChatOpen]);

  const sendQuestion = async (forcedQuestion) => {
    if (isSending) {
      return;
    }

    const finalQuestion = (forcedQuestion || question).trim();
    if (!finalQuestion) {
      return;
    }

    const nextMessages = [
      ...messages,
      { role: "user", content: finalQuestion },
    ];
    setMessages(nextMessages);
    setQuestion("");
    setIsSending(true);

    try {
      const response = await fetch("/.netlify/functions/recruiter-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId,
          pagePath:
            typeof window !== "undefined" ? window.location.pathname : "/",
          messages: nextMessages,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      const reply =
        payload && payload.reply
          ? payload.reply
          : createFallbackReply(finalQuestion);

      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: createFallbackReply(finalQuestion) },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const quickPrompts = [];

  return (
    <Grid item xs={12} className={classes.homeContainer}>
      <span id="Home" style={{ position: "absolute", top: 0 }}></span>
      <Container maxWidth="md" className={classes.contentWrapper}>
        <Typography className={classes.greeting}>Hello, I'm</Typography>
        <Typography variant="h1" className={classes.firstName}>
          Berkley
        </Typography>
        <Typography variant="h1" className={classes.lastName}>
          Olmstead
        </Typography>
        <Typography className={classes.caption}>
          {displayedText}
          <span className={classes.cursor}></span>
        </Typography>

        <LoomVideo
          loomUrl={mainLoomVideoUrl}
          inline
          iframeTitle="Berkley Olmstead intro video"
        />

        <div className={classes.buttonContainer}>
          <a href="#Experience" className={classes.link}>
            <Button className={classes.primaryButton}>View My Work</Button>
          </a>
          <a href="Resume.pdf" download className={classes.link}>
            <Button className={classes.secondaryButton}>Download Resume</Button>
          </a>
        </div>
      </Container>

      <div
        className={`${classes.chatWidget} ${
          isChatOpen ? classes.chatWidgetOpen : ""
        }`}
      >
        <div className={classes.chatHeader}>
          <div className={classes.chatHeaderText}>
            <Typography className={classes.chatTitle}>
              Berkley's AI Asisstant
            </Typography>
            <Typography className={classes.chatSubtitle}>
              Ask anything about me
            </Typography>
          </div>
          <Button
            className={classes.closeButton}
            onClick={() => setIsChatOpen(false)}
            aria-label="Close chat"
          >
            ×
          </Button>
        </div>

        <div className={classes.quickPromptRow}>
          {quickPrompts.map((prompt) => (
            <Button
              key={prompt}
              className={classes.quickPromptButton}
              onClick={() => sendQuestion(prompt)}
              disabled={isSending}
            >
              {prompt}
            </Button>
          ))}
        </div>

        <div className={classes.messageList} ref={messageListRef}>
          {messages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={`${classes.messageBubble} ${
                message.role === "user"
                  ? classes.userBubble
                  : classes.assistantBubble
              }`}
            >
              {message.content}
            </div>
          ))}
        </div>

        {isSending && (
          <Typography className={classes.loadingText}>Thinking...</Typography>
        )}

        <form
          className={classes.chatInputRow}
          onSubmit={(event) => {
            event.preventDefault();
            sendQuestion();
          }}
        >
          <input
            className={classes.chatInput}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Example: What experience does Berkley have with Salesforce and AWS?"
            disabled={isSending}
          />
          <Button
            type="submit"
            className={classes.sendButton}
            disabled={isSending || !question.trim()}
          >
            Ask
          </Button>
        </form>
      </div>

      <Button
        className={classes.chatLauncher}
        onClick={() => setIsChatOpen((prev) => !prev)}
      >
        {isChatOpen ? "Close Chat" : "Berkley's AI"}
      </Button>

      <a href="#Experience" className={classes.scrollIndicator}>
        <KeyboardArrowDownIcon style={{ fontSize: 40 }} />
      </a>
    </Grid>
  );
}
